import { ChangeDetectorRef, Component, effect, inject, input, TemplateRef, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateDirective, TranslatePipe as NgxTranslatePipe } from '@ngx-translate/core';
import { GoalRenameComponent, GoalTypeIconComponent } from '@tdwb/lazy/lti';
import {
  ModalService,
  ModalRef,
  InvestmentGoal,
  GoalStatusType,
  LtiGoalStatusTypeRouteResolver,
  GoalStatusTypeHelper,
  ButtonComponent,
  HrefDirective,
  DividerComponent,
  InlineIconWrapperComponent,
  CurrencyPipe,
  ConfirmComponent,
  ConfirmTitleComponent,
  ConfirmBodyComponent,
  ConfirmButtonsComponent,
  ConfirmButtonComponent,
  FlyoutMenuComponent,
  FlyoutMenuItemDirective,
  FlyoutMenuToggleDirective,
  IconComponent
} from '@tdwb/core';

import { DashboardComponent } from './dashboard.component';
import { DashboardGoalsStatusTooltipComponent } from './tooltip/dashboard-goals-status-tooltip.component';

@Component({
  selector: 'td-wb-lti-dashboard-goal',
  templateUrl: 'dashboard-goal.component.html',
  styleUrls: ['dashboard-goal.component.scss'],
  imports: [
    NgTemplateOutlet,
    TranslateDirective,
    NgxTranslatePipe,
    GoalTypeIconComponent,
    GoalRenameComponent,
    DashboardGoalsStatusTooltipComponent,
    ButtonComponent,
    HrefDirective,
    DividerComponent,
    InlineIconWrapperComponent,
    CurrencyPipe,
    ConfirmComponent,
    ConfirmTitleComponent,
    ConfirmBodyComponent,
    ConfirmButtonsComponent,
    ConfirmButtonComponent,
    FlyoutMenuComponent,
    FlyoutMenuItemDirective,
    FlyoutMenuToggleDirective,
    IconComponent
  ]
})

export class DashboardGoalComponent {
  private readonly dashboardComponent = inject(DashboardComponent);
  private readonly modalService = inject(ModalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly goal = input<InvestmentGoal | undefined>(undefined);
  readonly smallView = input<boolean>(false);
  private readonly confirmDeleteQuery = viewChild<TemplateRef<unknown>>('confirmDelete');
  confirmDelete: TemplateRef<unknown> | undefined;
  goalContinueState: string = '';
  goalSetupComplete: boolean = true;
  goalComplete: boolean = false;
  goalName: string | undefined;
  goalStatusClass: string = '';
  showProjectionAmount: boolean = true;
  deleteDisabled: boolean = false;
  actionMenuOpen: boolean = false;

  private confirmModal: ModalRef<unknown> | undefined;

  constructor() {
    effect(() => {
      const goal = this.goal();
      if (goal !== undefined) {
        this.goalName = goal.name;
      }

      if (goal?.status !== undefined) {
        this.goalInit(goal.status);
      }
    });
  }

  protected get currentGoal(): InvestmentGoal | undefined {
    return this.goal();
  }

  onDeleteClick(): void {
    const confirmDelete = this.confirmDelete ?? this.confirmDeleteQuery();
    if (confirmDelete === undefined) {
      return;
    }

    this.confirmModal = this.modalService.open(confirmDelete, {
      size: 'extra-small'
    });

    this.deleteDisabled = true;
    this.confirmModal.afterClose().subscribe((result: unknown): void => {
      this.deleteGoal(result !== 'no');
      this.changeDetectorRef.markForCheck();
    });
  }

  checkGoalCompleteStatus(): boolean {
    return this.goalSetupComplete && !this.goalComplete;
  }

  hasObjective(): boolean {
    const goal = this.goal();
    return goal !== undefined && goal.investmentObjective !== undefined;
  }

  actionMenuToggle(value: boolean): void {
    this.actionMenuOpen = value;
  }

  updateGoalName(goalName: string): void {
    this.goalName = goalName;
  }

  private goalInit(status: GoalStatusType): void {
    this.goalSetupComplete = GoalStatusTypeHelper.isGoalSetupComplete(status);
    this.goalComplete = GoalStatusTypeHelper.isGoalComplete(status);
    this.goalContinueState = LtiGoalStatusTypeRouteResolver.getStateNameToContinueGoal(status);
    this.goalStatusClass = this.getGoalClass(status);
    this.showProjectionAmount = this.goalSetupComplete && !this.goalComplete && this.goal()?.projectedAmount !== undefined;
  }

  private deleteGoal(close: boolean): void {
    const goal = this.goal();
    if (close && goal?.id !== undefined) {
      this.dashboardComponent.deleteGoal(goal.id).subscribe((): void => {
        this.deleteDisabled = false;
        this.changeDetectorRef.markForCheck();
      });
    } else {
      this.deleteDisabled = false;
    }
  }

  private getGoalClass(status: GoalStatusType): string {
    if (status === GoalStatusType.COMPLETE || status === GoalStatusType.GOOD) {
      return 'td-wb-dashboard-goal__status--green';
    } if (status === GoalStatusType.NEEDS_ATTENTION) {
      return 'td-wb-dashboard-goal__status--orange';
    }

    return '';
  }
}
